<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { filterOptions } from './filterOptions';

    const props = withDefaults(defineProps<{
        options: readonly string[];
        placeholder?: string;
        disabled?: boolean;
    }>(), {
        placeholder: '',
        disabled: false
    });

    const emit = defineEmits<{
        typing: [text: string];
    }>();

    /** The chosen option, or null when nothing is chosen. */
    const value = defineModel<string | null>({ default: null });

    /** The text in the box, which is also the filter. Bind it only when the text must be set from outside. */
    const search = defineModel<string>('search', { default: '' });

    /** The Buefy autocomplete, used to sync its own selection when the value is set from outside. */
    type Autocomplete = {
        selected: string | null;
        setSelected: (_option: string | null) => void;
    };

    const autocomplete = ref<Autocomplete | null>(null);

    const filteredOptions = computed(() => filterOptions(props.options, search.value));

    // Keep the box in step when the chosen value is changed from outside, e.g. a store reset
    watch(value, (current, previous) => {
        if (current === null) {
            if (search.value === previous) {
                search.value = '';
            }
            return;
        }

        if (autocomplete.value && autocomplete.value.selected !== current) {
            autocomplete.value.setSelected(current);
        }
    });

    function onSelect(option: string | null) {
        value.value = option ?? null;
    }

    function onTyping(text: string) {
        emit('typing', text);
    }
</script>

<template>
    <b-autocomplete
        ref="autocomplete"
        v-model="search"
        :data="filteredOptions"
        :placeholder="placeholder"
        :disabled="disabled"
        aria-role="list"
        append-to-body
        open-on-focus
        keep-first
        clearable
        :confirm-keys="['Enter']"
        @typing="onTyping"
        @select="onSelect">
        <template #empty>
            No matches
        </template>
    </b-autocomplete>
</template>
